import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import AgeRatioChart from 'components/Analytics/AgeRatioChart';
import useRangeChange from 'hooks/useRangeChange';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import React, { useEffect, useState } from 'react';
import { getFromUrl } from '../api/Api';
import CasesOverviewChart from '../components/Analytics/CasesOverviewChart';
import DefaultCard from '../components/Analytics/DefaultCard';
import GenderRatioChart from '../components/Analytics/GenderRatioChart';
import NationalityRatio from '../components/Analytics/NationalityRatio';
import SeverityPieChart from '../components/Analytics/SeverityPieChart';
import SymptomsChart from '../components/Analytics/SymptomsChart';
import Card from '../components/Dashboard/Card';
import TimeCard from '../components/Dashboard/TimeCard';
import DashboardSkeleton from '../components/Skeletons/DashboardSkeleton';
import { CasesOverview } from '../interface';
import DashboardLayout from '../layouts/dashboard/DashboardLayout';
import { colors } from '../theme/colors';
import { createGraphData } from '../utils/helper';
import {
  buildAnalyticsQuery,
  isAnalyticsQueryReady
} from '../utils/dateRange';
import {
  getCurrentRole,
  hasPermission,
  Permission
} from '../constants/roles';

const Analytics = () => {
  interface GraphInterface {
    name: string;
    value: number;
  }

  interface Count {
    counsellors: number;
    clients: number;
    female: number;
    male: number;
  }

  const [data, setData] = useState([] as GraphInterface[]);
  const [casesOverview, setCasesOverview] = useState({} as CasesOverview);
  const [usersOverview, setUsersOverview] = useState({} as Count);
  const [isDownloading, setIsDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const {
    range,
    startDate: analyticsStartDate,
    endDate: analyticsEndDate,
    handleChange,
    handleStartDateChange,
    handleEndDateChange
  } = useRangeChange();
  const canExport = hasPermission(getCurrentRole(), Permission.EXPORT_ANALYTICS);

  React.useEffect(() => {
    getUserOverview();
    getCasesOverview();
  }, [range, analyticsStartDate, analyticsEndDate]);

  const getUserOverview = async () => {
    try {
      const overview = await getFromUrl('users/overview');
      setUsersOverview(overview.count);
    } catch (e: any) {
      return;
    }
  };

  const getCasesOverview = async () => {
    if (!isAnalyticsQueryReady(range, analyticsStartDate, analyticsEndDate)) {
      setCasesOverview({} as CasesOverview);
      setData([]);
      return;
    }

    try {
      const query = buildAnalyticsQuery(
        range,
        analyticsStartDate,
        analyticsEndDate
      );
      const overview = await getFromUrl(
        `cases/metrics/comprehensive?${query}`
      );
      setCasesOverview(overview);
      const graphData = createGraphData(overview?.prevalentSymptoms);
      setData(graphData);
    } catch (e: any) {
      return;
    }
  };

  const { severity } = casesOverview || {};

  const exportCases = async () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Please select both a start and end date.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast({
        title: 'Start date must be before end date.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    setExporting(true);
    try {
      const query = `startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`;
      const blob = await getFromUrl(`cases/excel/export?${query}`, 'file');
      const excelBlob = new Blob([blob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(excelBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cases-export-${startDate}-to-${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      toast({
        title: 'Export failed. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
    setExporting(false);
  };

  const handleDownload = () => {
    setIsDownloading(true);
  };
  useEffect(() => {
    if (isDownloading) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      html2canvas(document.querySelector('#capture')! as HTMLElement).then(
        (canvas) => {
          const image = canvas.toDataURL('image/png', 1.0);
          const doc = new jsPDF('p', 'mm', 'a4');
          const pageWidth = doc.internal.pageSize.getWidth();
          const imgWidth = pageWidth - 20;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          doc.addImage(image, 'png', 10, 10, imgWidth, imgHeight);
          doc.save('itunu-analytics.pdf');
        }
      );
      setIsDownloading(false);
    }
  }, [isDownloading]);

  return (
    <DashboardLayout pageTitle={`Analytics`}>
      {Object.keys(usersOverview).length ? (
        <>
          {canExport && (
            <Flex justifyContent='flex-end' my='14px' px='24px' gap='12px'>
              <Button
                bg={colors.deep_blue}
                color={colors.white}
                onClick={onOpen}
                isDisabled={exporting}
                _hover={{ bg: colors.dark_green }}
              >
                {exporting ? 'Exporting...' : 'Export Cases'}
              </Button>
              <Button
                bg={colors.accent}
                color={colors.white}
                onClick={handleDownload}
                _hover={{ bg: colors.deep_blue }}
              >
                Download PDF
              </Button>
            </Flex>
          )}

          <Box id='capture' pb='54px' px='24px'>
            {/* Row 1: Summary Stats Cards */}
            <Grid gridTemplateColumns='repeat(3, 1fr)' gap='16px' mb='24px'>
              <Card
                h='90px'
                px='24px'
                bg={isDownloading ? colors.light_green : colors.white}
              >
                <DefaultCard
                  isDownloading={isDownloading}
                  no={usersOverview.counsellors}
                  text='Total Counsellors'
                  color={colors.deep_blue}
                  icon='Profile'
                  bold
                />
              </Card>
              <Card
                h='90px'
                px='24px'
                bg={isDownloading ? colors.light_green : colors.white}
              >
                <DefaultCard
                  isDownloading={isDownloading}
                  no={usersOverview.clients}
                  text='Total Clients'
                  color={colors.default_green}
                  icon='Profile'
                  bold
                />
              </Card>
              <Card
                h='90px'
                px='24px'
                bg={isDownloading ? colors.light_green : colors.white}
              >
                <TimeCard
                  no={
                    typeof casesOverview?.averageWaitTimeInHours === 'number'
                      ? casesOverview?.averageWaitTimeInHours
                      : 0
                  }
                  isDownloading={isDownloading}
                  compact
                />
              </Card>
            </Grid>

            {/* Row 2: Cases Overview Grouped Bar Chart */}
            <Box mb='24px'>
              <CasesOverviewChart />
            </Box>

            {/* Row 3: Severity Pie + Symptoms Bar */}
            <Grid gridTemplateColumns='1fr 1.5fr' gap='24px' mb='24px'>
              <SeverityPieChart
                severity={severity}
                isDownloading={isDownloading}
              />
              <SymptomsChart
                data={data}
                isDownloading={isDownloading}
                handleChange={handleChange}
                rangeValue={range}
                startDate={analyticsStartDate}
                endDate={analyticsEndDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />
            </Grid>

            {/* Row 4: Gender Ratio + Age Range */}
            <Grid gridTemplateColumns='1fr 1fr' gap='24px' mb='24px'>
              <GenderRatioChart />
              <AgeRatioChart />
            </Grid>

            {/* Row 5: Cases by Location */}
            <NationalityRatio />
          </Box>
        </>
      ) : (
        <DashboardSkeleton />
      )}

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Export Cases</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing='16px'>
              <FormControl isRequired>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type='date'
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>End Date</FormLabel>
                <Input
                  type='date'
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' mr='8px' onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg={colors.deep_blue}
              color={colors.white}
              onClick={exportCases}
              isDisabled={exporting || !startDate || !endDate}
              isLoading={exporting}
              _hover={{ bg: colors.dark_green }}
            >
              Export
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DashboardLayout>
  );
};

export default Analytics;
